import { truncateLeftRight } from '@/portainer/filters/filters';
import { GitCommitLink } from '@/react/portainer/gitops/GitCommitLink';

import { CopyButton } from '@@/buttons';

import { AutoUpdateIntervalLineItem } from './AutoUpdateIntervalLineItem';
import { LineItem } from './LineItem';
import { SourceLineItem } from './SourceLineItem';
import { GitReferenceErrorKind, GitReferenceStatus } from './useGitCheckStatus';
import {
  GitReferenceStackType,
  isFileCheckSupported,
} from './useGitReferenceStatus';
import { EffectiveGitReference } from './useResolvedGitConfig';
import { WorkflowLineItem } from './WorkflowLineItem';

export function GitReferenceLineItems({
  workflowId,
  stackType,
  config: { url, ref, configFilePath, commitId, sourceId },
  webhookUrl,
  errorKind,
  status: { isRefLoading, foundRef, isFileLoading, foundFile },
}: {
  workflowId: number;
  stackType: GitReferenceStackType;
  config: EffectiveGitReference;
  webhookUrl?: string;
  errorKind?: GitReferenceErrorKind;
  status: GitReferenceStatus;
}) {
  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm lg:grid-cols-2 xl:grid-cols-3">
      <WorkflowLineItem workflowId={workflowId} />
      {!!sourceId && <SourceLineItem sourceId={sourceId} />}

      <LineItem
        label="Repo"
        value={url || 'missing'}
        title={url || 'missing'}
        isLoading={isRefLoading}
        isValid={!!url && errorKind !== 'repo'}
        isError={!url || errorKind === 'repo'}
        data-cy="git-url"
      />
      <LineItem
        label="Ref"
        value={ref || 'missing'}
        title={ref || 'missing'}
        isLoading={isRefLoading}
        isValid={foundRef}
        isError={!ref || errorKind === 'ref'}
        data-cy="git-ref"
      />
      {isFileCheckSupported(stackType) && (
        <LineItem
          label="File"
          value={configFilePath || 'missing'}
          title={configFilePath || 'missing'}
          isLoading={isFileLoading}
          isValid={foundFile}
          isError={!configFilePath || errorKind === 'file'}
          data-cy="git-file-path"
        />
      )}
      {!!commitId && (
        <LineItem
          label="Commit"
          value={<GitCommitLink baseURL={url || ''} commitHash={commitId} />}
          title={commitId}
          data-cy="git-commit"
        />
      )}
      {!!sourceId && <AutoUpdateIntervalLineItem sourceId={sourceId} />}
      {!!webhookUrl && (
        <LineItem
          label="Webhook"
          value={
            <>
              <span data-cy="git-webhook-url">
                {truncateLeftRight(webhookUrl, 0, 10, 25)}
              </span>
              <CopyButton
                copyText={webhookUrl}
                color="light"
                data-cy="git-webhook-copy-button"
              >
                Copy link
              </CopyButton>
            </>
          }
          title="webhook"
          data-cy="git-webhook"
        />
      )}
    </div>
  );
}
